import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { EyeIcon, EyeSlashIcon, SunIcon, MoonIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import Logo from '../components/icons/Logo';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { MultiFactorResolver, getMultiFactorResolver } from 'firebase/auth';
import { auth } from '../lib/firebase';
import MFAVerification from '../components/auth/MFAVerification';

interface LoginFormData {
  email: string;
  password: string;
  displayName?: string;
}

const Login = () => {
  const { user, signInWithGoogle, signInWithEmail, signUpWithEmail, resetPassword } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'reset'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [searchParams] = useSearchParams();
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<LoginFormData>();

  useEffect(() => {
    const mode = searchParams.get('mode');
    // Regisztráció kivezetve – a ?mode=register sem nyitja meg
    void mode;
    const plan = searchParams.get('plan');
    if (plan) {
      localStorage.setItem('selectedPlan', plan);
    }
  }, [searchParams]);

  if (user) {
    // Customer szerepkör az ügyfélportálra irányít
    if (user.role === 'customer') {
      return <Navigate to="/customer-portal" replace />;
    }
    return <Navigate to="/" replace />;
  }

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    
    try {
      if (authMode === 'login') {
        await signInWithEmail(data.email, data.password);
      } else if (authMode === 'register') {
        await signUpWithEmail(data.email, data.password, data.displayName);
      } else if (authMode === 'reset') {
        await resetPassword(data.email);
        setAuthMode('login');
        reset();
      }
    } catch (error: any) {
      // Check if MFA is required
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error);
        setMfaResolver(resolver);
      }
      // Csak kritikus hibáknál logolunk
    } finally {
      setIsLoading(false);
    }
  };

  const handleMFASuccess = () => {
    setMfaResolver(null);
    // A bejelentkezés automatikusan megtörténik az MFA verifikáció után
  };

  const handleMFACancel = () => {
    setMfaResolver(null);
    setIsLoading(false);
  };

  const handleModeChange = (newMode: 'login' | 'register' | 'reset') => {
    setAuthMode(newMode);
    reset();
    setShowPassword(false);
  };

  const getTitle = () => {
    switch (authMode) {
      case 'login': return t('auth.login') || 'Bejelentkezés';
      case 'register': return t('auth.register') || 'Regisztráció';
      case 'reset': return t('auth.resetPassword') || 'Jelszó visszaállítása';
    }
  };

  const getSubtitle = () => {
    switch (authMode) {
      case 'login': return t('auth.loginSubtitle') || 'Jelentkezz be az ügyfeleid kezeléséhez';
      case 'register': return t('auth.registerSubtitle') || 'Hozz létre egy új fiókot';
      case 'reset': return t('auth.resetSubtitle') || 'Add meg az e-mail címed a jelszó visszaállításához';
    }
  };

  return (
    <div className="min-h-screen relative bg-[#f7f7f9] dark:bg-[#0b0d12] flex items-center justify-center overflow-hidden" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Theme and Language Selectors */}
      <div className="absolute top-4 right-4 z-20 flex items-center space-x-3">
        {/* Theme Selector */}
        <button
          onClick={toggleTheme}
          className="w-9 h-9 rounded-lg border border-gray-200 dark:border-[#2a2f3c] bg-white dark:bg-[#12151c] text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center"
          aria-label={theme === 'light' ? t('auth.switchToDark') : t('auth.switchToLight')}
        >
          {theme === 'light' ? (
            <MoonIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          ) : (
            <SunIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          )}
        </button>

        {/* Language Selector */}
        <button
          onClick={() => setLanguage(language === 'hu' ? 'en' : 'hu')}
          className="h-9 px-2.5 rounded-lg border border-gray-200 dark:border-[#2a2f3c] bg-white dark:bg-[#12151c] text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center gap-1"
          aria-label={language === 'hu' ? t('auth.switchToEnglish') : t('auth.switchToHungarian')}
        >
          <GlobeAltIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase">
            {language === 'hu' ? 'EN' : 'HU'}
          </span>
        </button>
      </div>

      {/* Nyugodt háttér – halvány teal fény, finom rács */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[#f7f7f9] dark:bg-[#0b0d12]" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[720px] h-[420px] rounded-full bg-[#0f7f75]/10 dark:bg-[#0f7f75]/15 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.035] dark:opacity-[0.06]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M 100 0 L 0 0 0 100' fill='none' stroke='%23000000' stroke-width='1'/%3E%3C/svg%3E")`, backgroundSize: '40px 40px' }} />
      </div>

      {/* Login container */}
      <div className="relative max-w-md w-full mx-4 my-6 sm:m-4">
        <div className="bg-white dark:bg-[#12151c] p-6 sm:p-8 rounded-2xl border border-gray-200 dark:border-[#2a2f3c] shadow-sm">
          <div className="text-center mb-6 sm:mb-8">
            <Logo className="h-12 sm:h-14 mx-auto mb-4" />
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-50 mb-1">{getTitle()}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{getSubtitle()}</p>
          </div>
          {/* Email/Password Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mb-6">
            {/* Display Name - only for register */}
            {authMode === 'register' && (
              <div>
                <label htmlFor="displayName" className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                  {t('auth.displayName') || 'Név'}
                </label>
                <input
                  type="text"
                  id="displayName"
                  {...register('displayName', {
                    required: authMode === 'register' ? (t('auth.displayNameRequired') || 'A név megadása kötelező') : false
                  })}
                  className="w-full h-11 px-3 text-base sm:text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75]"
                  placeholder={t('auth.displayNamePlaceholder')}
                />
                {errors.displayName && (
                  <p className="mt-1 text-sm text-red-600">{errors.displayName.message}</p>
                )}
              </div>
            )}

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                {t('auth.email') || 'E-mail cím'}
              </label>
              <input
                type="email"
                id="email"
                {...register('email', {
                  required: t('auth.emailRequired') || 'Az e-mail cím megadása kötelező',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('auth.invalidEmail') || 'Érvénytelen e-mail formátum'
                  }
                })}
                className="w-full h-11 px-3 text-base sm:text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75]"
                placeholder={t('auth.emailPlaceholder')}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Password - not for reset */}
            {authMode !== 'reset' && (
              <div>
                <label htmlFor="password" className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                  {t('auth.password') || 'Jelszó'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    {...register('password', {
                      required: t('auth.passwordRequired') || 'A jelszó megadása kötelező',
                      minLength: {
                        value: 6,
                        message: t('auth.passwordTooShort') || 'A jelszó legalább 6 karakter hosszú legyen'
                      }
                    })}
                    className="w-full h-11 px-3 pr-10 text-base sm:text-sm border rounded-lg bg-white dark:bg-[#0b0d12] border-gray-300 dark:border-[#2a2f3c] text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0f7f75]/40 focus:border-[#0f7f75]"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-11 inline-flex items-center justify-center rounded-lg bg-[#0f7f75] text-white text-sm font-medium hover:bg-[#0c6b62] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t('auth.processing') || 'Feldolgozás...'}
                </div>
              ) : (
                getTitle()
              )}
            </button>
          </form>

          {/* Forgot Password Link & Register Link */}
          {authMode === 'login' && (
            <div className="text-center mb-4 space-y-2">
              <button
                type="button"
                onClick={() => handleModeChange('reset')}
                className="block w-full text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {t('auth.forgotPassword') || 'Elfelejtetted a jelszavadat?'}
              </button>
              {/* Regisztráció kivezetve: csak meglévő fiókkal lehet belépni */}
            </div>
          )}

          {/* Back to Login from Reset or Register */}
          {(authMode === 'reset' || authMode === 'register') && (
            <div className="text-center mb-4">
              <button
                type="button"
                onClick={() => handleModeChange('login')}
                className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {t('auth.backToLogin') || '← Vissza a bejelentkezéshez'}
              </button>
            </div>
          )}

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200 dark:border-[#2a2f3c]" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-[#12151c] text-gray-500 dark:text-gray-400">
                {t('auth.or') || 'vagy'}
              </span>
            </div>
          </div>

          {/* Google Sign In Button */}
          <button
            onClick={async () => {
              try {
                setIsLoading(true);
                await signInWithGoogle();
              } catch (error: any) {
                // Check if MFA is required
                if (error.code === 'auth/multi-factor-auth-required') {
                  const resolver = getMultiFactorResolver(auth, error);
                  setMfaResolver(resolver);
                }
                // Csak kritikus hibáknál logolunk
              } finally {
                setIsLoading(false);
              }
            }}
            disabled={isLoading}
            className="w-full h-11 inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-[#2a2f3c] bg-white dark:bg-[#0b0d12] text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t('auth.signInWithGoogle') || 'Bejelentkezés Google-fiókkal'}
          </button>

          {/* Privacy Policy & Terms */}
          <div className="text-center text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            <p className="mb-2">
              {t('auth.acceptTerms') || 'A folytatással elfogadod a YouCRM'}
            </p>
            <div className="space-x-1">
              <Link 
                to="/terms-of-service" 
                className="text-[#0f7f75] hover:underline dark:text-teal-300 transition-colors duration-200"
              >
                {t('auth.termsOfService') || 'Felhasználási feltételeit'}
              </Link>
              <span className="text-gray-500 dark:text-gray-400">{t('auth.and') || 'és'}</span>
              <Link 
                to="/privacy-policy" 
                className="text-[#0f7f75] hover:underline dark:text-teal-300 transition-colors duration-200"
              >
                {t('auth.privacyPolicy') || 'Adatvédelmi szabályzatát'}
              </Link>
            </div>
          </div>
        </div>

        {/* Fejlesztő */}
        <div className="mt-5 flex justify-center">
          <a href="https://webissimo.eu" target="_blank" rel="noopener noreferrer" className="opacity-70 hover:opacity-100 transition-opacity" aria-label="Webissimo Kft.">
            <img src="/webissimo.svg" alt="Webissimo" className="h-4 w-auto invert dark:invert-0" />
          </a>
        </div>
      </div>

      {/* MFA Verification Modal */}
      {mfaResolver && (
        <MFAVerification
          resolver={mfaResolver}
          onSuccess={handleMFASuccess}
          onCancel={handleMFACancel}
        />
      )}
    </div>
  );
};

export default Login; 